import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import Modal, {ModalBody, ModalFooter, ModalHeader, ModalTitle,} from '../../../../components/bootstrap/Modal';
import {useTranslation} from 'react-i18next';
import Button from '../../../../components/bootstrap/Button';
import Icon from '../../../../components/icon/Icon';
import Input from '../../../../components/bootstrap/forms/Input';
import Label from '../../../../components/bootstrap/forms/Label';
import SortableTree, {toggleExpandedForAll} from '@nosferatu500/react-sortable-tree';
import FileExplorerTheme from '@nosferatu500/theme-file-explorer';
import Checks from '../../../../components/bootstrap/forms/Checks';
import {useMst} from '../../../../models';
import DepartmentService from '../../../../services/DepartmentService';
import {getLineUser} from '../../../../lib/Util';
import ThemeContext from 'contexts/themeContext';
import {
  buildTreeUserMap, getLineUserDept, getLineUserName, getLineUserRank,
  getTreeDeptName, getTreeUserName, getTreeUserRank, matchesTreeSearch,
} from 'helpers/orgTreeI18n';

const SortableTreeModal = ({ title, isOpen, setIsOpen, disabledList, list, setList }) => {
  const [departmentTree, setDepartmentTree] = useState([]);
  const [treeUser, setTreeUser] = useState([]);
  const [disableuserList, setDisableUserList] = useState([]);
  const [selectedTreeUser, setSelectedTreeUser] = useState([]);
  const [timer, setTimer] = useState(0);
  const { company } = useMst();
  const searchRef = useRef(null);
  const { t } = useTranslation();
  const { mobileDesign } = useContext(ThemeContext);
  const treeUserMap = useMemo(() => buildTreeUserMap(treeUser), [treeUser]);

  const applyTreeLines = () => {
    const tree = document.querySelector('.cc-sortable-tree');
    if (!tree) return;

    const VDASH = 'repeating-linear-gradient(to bottom,#a78bfa 0,#a78bfa 3px,transparent 3px,transparent 6px)';
    const HDASH = 'repeating-linear-gradient(to right,#a78bfa 0,#a78bfa 3px,transparent 3px,transparent 6px)';

    const wrappers = Array.from(tree.querySelectorAll('.rstcustom__rowWrapper'));
    wrappers.forEach((wrapper, idx) => {
      const flexDiv = wrapper.firstElementChild;
      if (!flexDiv) return;

      const rowEl = flexDiv.querySelector('.rstcustom__row');
      const isPerson = rowEl && rowEl.classList.contains('cc-node-person');

      const lineBlocks = Array.from(flexDiv.querySelectorAll(':scope > .rstcustom__lineBlock'));
      lineBlocks.forEach(el => {
        el.style.backgroundImage = 'none';
        el.style.backgroundSize = '';
        el.style.backgroundPosition = '';
        el.style.backgroundRepeat = '';
        el.style.height = '';
      });

      if (!isPerson || lineBlocks.length === 0) return;

      const nextWrapper = wrappers[idx + 1];
      let isLastChild = true;
      if (nextWrapper) {
        const nextFlex = nextWrapper.firstElementChild;
        if (nextFlex) {
          const nextBlocks = nextFlex.querySelectorAll(':scope > .rstcustom__lineBlock');
          const nextRow = nextFlex.querySelector('.rstcustom__row');
          const nextIsPerson = nextRow && nextRow.classList.contains('cc-node-person');
          if (nextBlocks.length === lineBlocks.length && nextIsPerson) isLastChild = false;
        }
      }

      const rowHeight = rowEl.getBoundingClientRect().height || 27;
      const last = lineBlocks[lineBlocks.length - 1];
      last.style.height = rowHeight + 'px';
      const vSize = isLastChild ? '2px 50%' : '2px 100%';
      last.style.backgroundImage = `${VDASH}, ${HDASH}`;
      last.style.backgroundSize = `${vSize}, 17px 2px`;
      last.style.backgroundPosition = '8px 0, 8px 50%';
      last.style.backgroundRepeat = 'no-repeat, no-repeat';
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const timers = [setTimeout(applyTreeLines, 150), setTimeout(applyTreeLines, 400)];
    const observer = new MutationObserver(() => applyTreeLines());
    const tree = document.querySelector('.cc-sortable-tree');
    if (tree) observer.observe(tree, { childList: true, subtree: true });
    return () => { timers.forEach(clearTimeout); observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, departmentTree]);

  const getTreeUser = async () => {
    const response = await DepartmentService.getDepartmentTreeUser(company.get.id, 'SUCCESS', null, true);
    if (response?.data?.data) {
      const treeUsers = toggleExpandedForAll({ treeData: response.data.data, expanded: true });
      setTreeUser(treeUsers);
      setDepartmentTree(treeUsers);
    }
  };

  const debounce = (e) => {
    const time = 1000;
    if (timer) clearTimeout(timer);

    if (e.target.value !== '') {
      const newTimer = setTimeout(() => {
        const searchText = e.target.value;
        const filterTree = (node) => {
          const { children = [], ...rest } = node;
          const isMatch = matchesTreeSearch(t, rest, searchText);
          const filteredChildren = children.map(filterTree).filter(Boolean);
          if (isMatch || filteredChildren.length > 0) return { ...node, children: filteredChildren };
          return null;
        };
        const filtered = treeUser.map(filterTree).filter(Boolean);
        setDepartmentTree(toggleExpandedForAll({ treeData: filtered, expanded: true }));
      }, time);
      setTimer(newTimer);
    } else {
      setDepartmentTree([...treeUser]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setDisableUserList(getLineUser(disabledList));
      setSelectedTreeUser([...list]);
    } else {
      setSelectedTreeUser([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    getTreeUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} size='lg' isCentered isStaticBackdrop={true} modalClassNameProps='sortable_modal' fullScreen={mobileDesign ? true : false}>
      <ModalHeader setIsOpen={setIsOpen} className='px-4 pt-4 pb-2' style={{ borderBottom: 'none' }}>
        <div className='w-100'>
          <ModalTitle>{title}</ModalTitle>
          <p className='text-muted mb-0 mt-1' style={{ fontSize: '0.82rem' }}>
            {t('chooseCCpopup.cannotSelectPeopleInApprovalLine')}
          </p>
        </div>
      </ModalHeader>

      <ModalBody className='px-0 py-0' style={{ overflow: 'hidden' }}>
        <div className='d-flex' style={{ height: mobileDesign ? '100%' : '65vh', flexDirection: mobileDesign ? 'column' : 'row' }}>

          {/* 왼쪽: 조직도 */}
          <div className='d-flex flex-column px-4 py-3' style={{ flex: mobileDesign ? '0 0 72%' : '0 0 55%', overflow: 'hidden' }}>
            <div className='mb-3' style={{ fontWeight: 700, fontSize: '1.1rem' }}>{t('조직도')}</div>

            <div className='search_box no_txt'>
              <Label htmlFor='cc-tree-search' className='visually-hidden'>{t('검색')}</Label>
              <Input
                ref={searchRef}
                id='cc-tree-search'
                type='text'
                placeholder={t('이름, 사번, 부서 검색')}
                onChange={(e) => debounce(e)}
              />
            </div>

            <div className='cc-sortable-tree' style={{ flex: 1, overflow: 'hidden', minHeight: 0, paddingTop: '0.75rem' }}>
              {departmentTree.length > 0 && (
                <SortableTree
                  style={{ height: '100%' }}
                  treeData={departmentTree}
                  onChange={setDepartmentTree}
                  maxDepth={10}
                  getNodeKey={({ node }) => node.id}
                  canDrag={false}
                  theme={FileExplorerTheme}
                  generateNodeProps={(nodes) => {
                    const isPerson = !!nodes.node?.userId;
                    const isSelected = selectedTreeUser.map(i => i.userId).includes(nodes?.node?.userId);
                    const isDisabled = !!disableuserList.find(d => d.userId === nodes?.node?.userId);

                    return {
                      className: isPerson ? 'cc-node-person' : 'cc-node-dept',
                      style: isPerson && isSelected ? { backgroundColor: '#eef2ff', borderRadius: 6 } : {},
                      title: isPerson ? (
                        <div
                          className='d-flex align-items-center w-100'
                          style={{
                            gap: 6, minWidth: 0, marginLeft: 6,
                            opacity: isDisabled ? 0.45 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'default',
                          }}
                        >
                          <Checks
                            type='checkbox'
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => {
                              const currentUsers = [...selectedTreeUser];
                              const data = {
                                rankId: nodes?.node?.userRankData?.id || 0,
                                userId: nodes?.node?.userId,
                                rank: nodes?.node?.userRank,
                                companyId: nodes?.node?.companyid,
                                positionId: nodes?.node?.userPositionData?.id || 0,
                                departmentId: nodes?.node?.userDepartmentsData?.departmentId || 0,
                                department: nodes?.node?.userDepartment,
                                head: nodes?.node?.userDepartmentData?.head || false,
                                name: nodes?.node?.name,
                                title: nodes?.node?.title,
                                // 다국어 이름(화면 표시용)
                                nameEngFirst: nodes?.node?.nameEngFirst,
                                nameEngLast: nodes?.node?.nameEngLast,
                                nameVie: nodes?.node?.nameVie,
                                nameChn: nodes?.node?.nameChn,
                              };
                              const index = currentUsers.findIndex(u => parseInt(u.userId) === parseInt(nodes?.node?.userId));
                              if (index > -1) currentUsers.splice(index, 1);
                              else currentUsers.push(data);
                              setSelectedTreeUser([...currentUsers]);
                            }}
                          />
                          <Icon
                            icon={isDisabled ? 'Lock' : 'Person'}
                            style={{ color: isDisabled ? '#adb5bd' : '#6c757d', fontSize: '1rem', flexShrink: 0 }}
                          />
                          <span style={{ fontWeight: 600, fontSize: '0.88rem', color: isDisabled ? '#adb5bd' : undefined }}>
                            {getTreeUserName(nodes.node)}
                          </span>
                          {nodes.node.userRank && (
                            <span className='text-muted' style={{ fontSize: '0.78rem' }}>| {getTreeUserRank(t, nodes.node)}</span>
                          )}
                          <span className='flex-grow-1' />
                          {isDisabled && (
                            <span style={{ fontSize: '0.72rem', color: '#adb5bd', whiteSpace: 'nowrap' }}>
                              {t('결재라인')}
                            </span>
                          )}
                          {!isDisabled && nodes?.node?.companyNumber && (
                            <span className='text-muted' style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              ({nodes?.node?.companyNumber})
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className='d-flex align-items-center' style={{ gap: 6, marginLeft: 3 }}>
                          <Icon icon='Folder' size='lg' style={{ color: '#f4a825' }} />
                          <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>{getTreeDeptName(nodes.node)}</span>
                        </div>
                      ),
                    };
                  }}
                />
              )}
            </div>
          </div>

          {/* 오른쪽: 참조자 */}
          <div
            className='d-flex flex-column px-4 py-3'
            style={{ flex: 1, overflow: 'hidden', backgroundColor: '#f8f9fa', borderLeft: '1px solid #e9ecef' }}>

            <div className='d-flex align-items-center mb-3' style={{ gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{t('참조자')}</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 24, height: 24, borderRadius: 12, padding: '0 6px',
                backgroundColor: '#4361ee', color: '#fff', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
              }}>
                {selectedTreeUser.length}
              </span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedTreeUser.map((user, index) => (
                <div
                  key={`cc-user-${user.userId}-${index}`}
                  className='position-relative'
                  style={{
                    padding: '10px 36px 10px 14px',
                    border: '1px solid #e9ecef',
                    borderRadius: 10,
                    backgroundColor: '#E7EEF8',
                    flexShrink: 0,
                  }}>
                  <div className='d-flex align-items-center' style={{ gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{getLineUserName(user, user, treeUserMap) || user.title || ''}</span>
                    {user.rank && <span className='text-muted' style={{ fontSize: '0.78rem' }}>| {getLineUserRank(t, user, user, treeUserMap)}</span>}
                  </div>
                  {user.department && (
                    <div className='text-muted' style={{ fontSize: '0.78rem', marginTop: 2 }}>{getLineUserDept(user, user, treeUserMap)}</div>
                  )}
                  <button
                    type='button'
                    className='btn btn-sm btn-only-icon position-absolute top-0 end-0'
                    onClick={() => {
                      const currentUsers = [...selectedTreeUser];
                      const idx = currentUsers.findIndex(u => parseInt(u.userId) === parseInt(user.userId));
                      if (idx > -1) { currentUsers.splice(idx, 1); setSelectedTreeUser([...currentUsers]); }
                    }}>
                    <Icon icon='Close' className='btn-icon' />
                  </button>
                </div>
              ))}

              {selectedTreeUser.length === 0 && (
                <div className='d-flex flex-column align-items-center justify-content-center text-muted' style={{ flex: 1, fontSize: '0.85rem', gap: 8 }}>
                  <Icon icon='PersonOff' style={{ fontSize: '2rem', color: '#dee2e6' }} />
                  <span>{t('적용 인원이 없습니다.')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className='d-flex justify-content-between align-items-center px-4 py-3' style={{ borderTop: '1px solid #e9ecef' }}>
        <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#495057' }}>
          {t('총')}{' '}
          <strong style={{ color: '#4361ee' }}>{selectedTreeUser.length}</strong>
          {t('명')} {t('선택')}
        </span>
        <Button
          color='info'
          style={{ borderRadius: 8, padding: '6px 28px', fontWeight: 600, fontSize: '1rem' }}
          onClick={() => {
            setList([...selectedTreeUser]);
            setIsOpen(false);
          }}>
          {t('대문자적용')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SortableTreeModal;
